import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getMenus, getSeedRequiredDataMode } from "store/userSlice";
import {
  fetchBudgetSettings,
  getBudgetSettings,
  getBudgetSettingsError
} from "store/budgetSettingsSlice";
import { api } from "helpers/axios";
import * as Sentry from "@sentry/react";
import { toast } from "helpers/toast";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { errorMessage, formatDateTime, formatInputNumber, formatMoney, formatNumberTo, formatTime, nl2br, scrollToTop } from "helpers/common";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { helpTextIcon } from "helpers/helpText";
import TextTruncate from "components/ui/TextTruncate";
import { tableResize } from "helpers/tableResize";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import usePinnedHead from "hooks/usePinnedHead";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import sanitize from "helpers/sanitize";
import { fetchSuggestedActions } from "store/suggestedActionsSlice";
import {
  getValidationEntries,
  VALIDATION_STATE_FAILED,
  VALIDATION_STATE_NOT_APPLICABLE,
} from "./preliminaryCheckValidation";
import { Button } from "components/ui";
dayjs.extend(customParseFormat);

const altFixErrors = {
  budget_entered: 'Der Budgetbetrag muss größer als null sein.',
  employees_in_budget: 'Dem Budget muss mindestens eine Person mit einer Leistungskarte zugewiesen sein.',
  employee_fixed_amount_less_than_capping: 'Der Festbetrage bei Beschäftigten muss unter dem jeweiligen Deckelungsbetrag liegen.',
  employees_have_individual_ceiling: 'Bei aktivierter Einstellung „Individueller Deckelungsbetrag“ muss für alle Beschäftigten ein Deckelungsbetrag hinterlegt sein.',
  employees_have_resolvable_capping_payfactor: 'Für alle Beschäftigten muss ein bestimmbarer Deckelungs-Entgeltfaktor vorhanden sein.',
  total_fixed_amount_less_than_budget: 'Die Gesamtsumme der Festbeträge aller Beschäftigten darf den Budgetbetrag nicht überschreiten.',
  work_hours_required_if_part_time_enabled: 'Soll die Arbeitszeit bei der Berechnung berücksichtigt werden, muss das Feld „Arbeitszeit“ ausgefüllt sein.',
  employment_entry_date_required_if_minimum_employment_enabled: 'Das Feld "Beschäftigt seid" muss ausgefüllt werden, wenn die Beschäftigungsdauer bei der Berechnung berücksicht werden soll.',
  employee_balanced_scorecards_total_weighting: 'Die Summe der Gewichtungen aller Leistungskriterien muss stets 100 % betragen.',
  budget_requires_days_and_months_if_absence_considered: 'Die Budgeteinstellungen sind vollständig (regelmäßige Arbeitsmonate/-tage).',
  budget_requires_months_for_preliminary_calculation: 'Die Berechnung benötigt die Eingabe von Fehlmonaten.',
  missing_months_required_for_preliminary_calculation: 'Die Eingabe der Fehlmonate ist nicht vollständig.',
  missing_days_required_if_absence_enabled: 'Die Eingabe der Fehltage ist nicht vollständig.',
  working_days_per_week_required_if_absence_enabled: 'Die Eingaben der Arbeitstage pro Woche sind nicht vollständig.',
  result_required_if_minimum_performance_enabled: 'Alle Leistungskarten sind vollständig bearbeitet.',
  balanced_scorecards_completed: 'Alle Leistungskarten sind abgeschlossen.',
  employees_have_payfactor: 'Jede Leistungskarte muss einen Entgeltfaktor enthalten.',
  employees_have_annual_compensation: 'Für alle Beschäftigten muss die Jahresvergütung ausgefüllt sein.',
  employees_have_pay_grade_and_level: 'Allen Beschäftigten wurde eine Entgeltgruppe und -stufe zugewiesen.',
};

function StatusBadge({ budget }) {

  const { editableTranslate } = useTranslation();

  return <>
    {
      // use pills for status, status is pending, in_progress, completed, failed, not_started
      !budget.current_process && <span className="badge badge-pill badge-secondary">{editableTranslate('calculation_overview.status.not_started', 'Nicht gestartet')}</span>
    }
    {
      budget.current_process?.status === 'pending' && <>
        <span className="badge badge-pill badge-warning">{editableTranslate('calculation_overview.status.pending', 'Ausstehend')}</span>
      </>
    }
    {
      budget.current_process?.status === 'in_progress' && <>
        <span className="badge badge-pill badge-primary">{editableTranslate('calculation_overview.status.in_progress', 'Gestartet')}</span>
      </>
    }
    {
      budget.current_process?.status === 'completed' && budget.current_process?.type === 'final_calculation' && <>
        <span className="badge badge-pill badge-success">{editableTranslate('calculation_overview.status.completed', 'Abgeschlossen')}</span>
      </>
    }
    {
      budget.current_process?.status === 'completed' && budget.current_process?.type === 'pre_calculation' && <>
        <span className="badge badge-pill badge-purple">{editableTranslate('calculation_overview.status.pre_completed', 'Vorprüfung abgeschlossen')}</span>
      </>
    }
    {
      budget.current_process?.status === 'failed' && <span className="badge badge-pill badge-danger">{editableTranslate('calculation_overview.status.failed', 'Fehlgeschlagen')}</span>
    }
  </>;
}

function LogListItem({ selectedBudget, logKey, logData = {}, state }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { editableTranslate, editableTranslateString } = useTranslation();

  const menus = useSelector(getMenus);
  const canAccessEmployeeManagement = !!menus?.some(m => m.path === '/employee-management');

  const [expanded, setExpanded] = useState(false);
  const failed = state === VALIDATION_STATE_FAILED;
  const notApplicable = state === VALIDATION_STATE_NOT_APPLICABLE;

  const handleClick = () => {
    if (failed) {
      setExpanded(!expanded);
    }
  };

  const handleLinkClick = async (e) => {
    e.preventDefault();

    let queries = `?source=calculation&error_key=${logKey}&budget_hash_id=${selectedBudget.hash_id}&budget_name=${encodeURIComponent(selectedBudget.name)}`;

    let pathName = '/employee-management';

    if (logKey === 'budget_entered' || logData?.model === 'budget') {
      pathName = '/settings/budgets';
      if (selectedBudget.budget_group) {
        queries += `&budget_group_hash_id=${selectedBudget.budget_group.hash_id}`;
      }
    }

    if (logData?.suggested_action_hash_id) {
      await dispatch(fetchSuggestedActions());

      pathName = `/settings/actions/suggested/${logData?.suggested_action_hash_id}`;
    }

    navigate({ pathname: pathName, search: queries });
  };

  return <li data-testid={`validation-item-${logKey}`} className={`list-group-item ${failed ? 'bg-red-dark-soft' : ''} ${notApplicable ? 'text-muted' : ''}`}>
    <div className={`d-flex justify-content-between align-items-center ${failed ? 'cursor-pointer' : ''}`} onClick={handleClick} data-testid="validation-toggle">
      {editableTranslate('calculation_overview.fix_' + logKey, altFixErrors[logKey] ?? '')}
      <div>
        {
          failed ?
            <span className="badge badge-danger badge-pill">{editableTranslate('calculation_overview.error')}</span> :
            notApplicable ?
              <span className="badge badge-pill badge-secondary">{editableTranslate('calculation_overview.not_applicable', 'Nicht zutreffend')}</span> :
              <span className="badge badge-success badge-pill">{editableTranslate('calculation_overview.ok')}</span>
        }
      </div>
    </div>
    {
      (expanded && failed) && <div className="mt-1">
        {
          (!['calculation_engine', 'budget_entered'].includes(logKey) && logData?.model !== 'budget' && !logData?.suggested_action_hash_id && !canAccessEmployeeManagement) ? <ul className="pl-3">
            <li>{editableTranslate('calculation_overview.no_employee_management_access', 'Für die Behebung dieses Problems ist Zugriff auf das Personalverwaltungssystem erforderlich.')}</li>
          </ul> : <>
            <div className="div-html break-word mt-2 mb-1" dangerouslySetInnerHTML={{ __html: sanitize(nl2br(editableTranslateString('calculation_overview.error_' + logKey + '_explain', 'Die Vorprüfung muss geklärt sein.'))) }} />
            <ul className="pl-3">
              {
                logKey === 'calculation_engine' ? (
                  <b>{editableTranslate('calculation_overview.error_message.' + logData.message, logData.error?.description)}</b>
                ) : <>
                  {
                    (logKey === 'budget_entered' || logData?.model === 'budget') ? <li><a href="#" onClick={(e) => handleLinkClick(e)} className="red-dark-link" data-testid="show-in-budgets-link">{editableTranslate('calculation_overview.show_in_budgets')}</a></li>
                      : logData?.suggested_action_hash_id ? <li><a href="#" onClick={(e) => handleLinkClick(e)} className="red-dark-link" data-testid="show-in-action-link">{editableTranslate('calculation_overview.show_in_action')}</a></li>
                        : <li><a href="#" onClick={(e) => handleLinkClick(e)} className="red-dark-link" data-testid="show-in-employee-overview-link">{editableTranslate('calculation_overview.show_in_employee_overview')}</a></li>
                  }
                </>
              }
            </ul>
          </>
        }
      </div>
    }
  </li>;
}

export default function PreliminaryCheck() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const { helps } = useOutletContext();

  const { translate, editableTranslate } = useTranslation();

  const budgetError = useSelector(getBudgetSettingsError);
  const budgets = useSelector(getBudgetSettings);
  const seedRequiredDataMode = useSelector(getSeedRequiredDataMode);

  const [budgetsData, setBudgetsData] = useState([]);
  const [debugData, setDebugData] = useState(null);

  const [error, setError] = useState(null);

  const [enableBatchCalculate, setEnableBatchCalculate] = useState(false);
  const [batchCalculateCheckedList, setBatchCalculateCheckedList] = useState([]);
  const [batchCalculateAll, setBatchCalculateAll] = useState(false);

  const [selectedBudget, setSelectedBudget] = useState(null);
  const [selectedBudgetHistory, setSelectedBudgetHistory] = useState(null);

  const validationLogEntries = useMemo(
    () => getValidationEntries(selectedBudget?.current_process?.log, { includeNotApplicable: true }),
    [selectedBudget?.current_process?.log]
  );
  const reportedUnknownValidationEntries = useRef(new Set());

  useEffect(() => {
    validationLogEntries
      .filter(({ hasUnknownState }) => hasUnknownState)
      .forEach(({ key, data }) => {
        const reportKey = `${selectedBudget?.current_process?.hash_id || 'unknown-process'}:${key}`;

        if (reportedUnknownValidationEntries.current.has(reportKey)) return;

        reportedUnknownValidationEntries.current.add(reportKey);
        Sentry.captureMessage('Unknown calculation validation state', {
          level: 'warning',
          extra: {
            validation_key: key,
            validation_state: data?.state ?? null,
            has_legacy_ok: typeof data?.ok === 'boolean',
          },
        });
      });
  }, [selectedBudget?.current_process?.hash_id, validationLogEntries]);

  const [calculations, setCalculations] = useState([]);

  const [lastFetchTime, setLastFetchTime] = useState(null);

  const [sortCols, setSortCols] = useState([['name', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);
  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const tableRef = useRef(null);

  const objSortCols = useMemo(() => {
    const obj = {};

    for (const sc of sortCols) {
      obj[sc[0]] = sc[1];
    }

    return obj;
  }, [sortCols]);

  const examinationHelp = useMemo(() => {
    if (helps) {
      return helps.filter(h => h.key === 'calculation-overview-examination')[0] || null;
    }
    return null;
  }, [helps]);

  const getLabel = (key, fallback) => {
    key = 'calculation_overview.' + key.replace('calculation_overview.', '');
    const sKey = key.replaceAll('.', '-');
    const help = helps && helps.filter(h => h.key === sKey)[0] || null;

    return <>
      {editableTranslate(key, fallback)}
      {help && helpTextIcon(sKey, help.short_text)}
    </>
  };

  const overview = useMemo(() => {
    if (!budgetsData || !Array.isArray(budgetsData)) return null;

    let last = null;
    let totalCalculated = 0;
    let totalNotCalculated = 0;
    let totalFailed = 0;
    let calculatedPercent = 0;

    for (const budget of budgetsData) {
      if (!budget?.current_process || (!budget.calculation_processes?.pre_calculation?.hash_id && !budget.calculation_processes?.final_calculation?.hash_id)) {
        totalNotCalculated++;
      }

      if (budget.calculation_processes?.final_calculation?.status === 'completed') {
        totalCalculated++;

        if (!last || dayjs(budget.calculation_processes?.final_calculation?.last_calculation_at).unix() > dayjs(last?.last_calculation_at).unix()) {
          last = {
            name: budget.name,
            last_calculation_at: budget.calculation_processes?.final_calculation?.last_calculation_at
          };
        }
      }

      if (budget.calculation_processes?.pre_calculation?.status === 'failed' || budget.calculation_processes?.final_calculation?.status === 'failed') {
        totalFailed++;
      }
    }

    if (totalCalculated > 0) {
      calculatedPercent = formatInputNumber(formatNumberTo((totalCalculated / budgetsData.length * 100) + ''));
    }

    return {
      total: budgetsData.length,
      last,
      total_calculated: totalCalculated,
      total_not_calculated: totalNotCalculated,
      total_failed: totalFailed,
      calculated_percent: calculatedPercent
    };
  }, [budgetsData]);

  const pinnedHeadRef = useRef(null);
  const theadRef = useRef(null);
  usePinnedHead(pinnedHeadRef, theadRef);

  useEffect(() => {
    if (budgetError) setError(budgetError);
  }, [budgetError]);

  useEffect(() => {
    fetchCalculations();

    const fetchBudgetsAndSetLastFetchTime = (background = false) => {
      fetchAllBudgets(background);
      setLastFetchTime(dayjs().format('HH:mm:ss'));
    };

    fetchBudgetsAndSetLastFetchTime();

    // Set an interval to fetch budgets every 5 seconds
    const interval = setInterval(() => {
      fetchBudgetsAndSetLastFetchTime(true);
    }, 5000);

    // Clear the interval on component unmount
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!budgets) return;

    const queryHashId = searchParams.get('hash_id');

    // Create a new budgets array to avoid mutating the original state
    const updatedBudgets = budgets.map(budget => {
      const newBudget = { ...budget, calculation_processes: budget.calculation_processes ? { ...budget.calculation_processes } : {} };

      let currentProcess = null;

      if (!newBudget.calculation_processes.pre_calculation) {
        newBudget.calculation_processes.pre_calculation = {
          hash_id: null,
          type: "pre_calculation",
          status: null,
          log: null,
          started_at: null,
          last_calculation_at: null
        };
      } else {
        currentProcess = { ...newBudget.calculation_processes.pre_calculation };
      }

      if (!newBudget.calculation_processes.final_calculation) {
        newBudget.calculation_processes.final_calculation = {
          hash_id: null,
          type: "final_calculation",
          status: null,
          log: null,
          started_at: null,
          last_calculation_at: null
        };
      } else {
        if (!currentProcess || (newBudget.calculation_processes.final_calculation.status && newBudget.calculation_processes.final_calculation.status !== 'pending')) {
          currentProcess = { ...newBudget.calculation_processes.final_calculation };
        }
      }

      newBudget.current_process = currentProcess;

      if (queryHashId) {
        if (queryHashId === newBudget.hash_id) {
          setSelectedBudget({ ...newBudget });
          fetchCalculationHistory(newBudget, true);
        }
      } else {
        if (selectedBudget && selectedBudget.hash_id === newBudget.hash_id) {
          setSelectedBudget({ ...newBudget });
          fetchCalculationHistory(newBudget, true);
        }
      }

      return newBudget;
    });

    sortBudgets(updatedBudgets, sortCols);

    setBudgetsData(updatedBudgets);
  }, [budgets, sortCols]);

  const minColWidth = {
    status: 200
  };

  const maxColWidth = {
    name: 300,
    group: 300,
    amount: 300,
    status: 300,
    last_calculation: 300
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    if (!enableBatchCalculate) {
      tableResize(table, 'preliminary_check', false, minColWidth, maxColWidth, setResizedCount);
    }
  }, [budgetsData, triggerColWidth, enableBatchCalculate, selectedBudget]);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [selectedBudget]);

  const fetchAllBudgets = (background = false) => {
    dispatch(fetchBudgetSettings({ include: 'calculation_process', background }));
  };

  // background: reload after a budgets fetch, keeps the current error and scroll position
  const fetchCalculationHistory = async (budget, background = false) => {
    if (!background) setError(null);
    try {
      const { data, status } = await api.get(`/calculation-engine/calculation-histories/${budget.hash_id}`);
      if (status === 200) {
        setSelectedBudgetHistory(data.histories);
        if (!background) scrollToTop();
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch calculation history'));
      Sentry.captureException(err);
    }
  };

  const handlePreCalculation = async (budget) => {
    setError(null);

    try {
      const { status } = await api.post(`/calculation-engine/preliminary-calculation`, {
        budget_setting_hash_id: budget.hash_id,
      });
      if (status === 200) {
        toast.success(translate('calculation_overview.calculation_triggered'));
        scrollToTop();
      }
    } catch (err) {
      setError(errorMessage(err, 'trigger pre calculation'));
      Sentry.captureException(err);
    }

    fetchAllBudgets();
  };

  const handleFinalCalculation = async (budget) => {
    setError(null);

    try {
      const { status } = await api.post(`/calculation-engine/final-calculation`, {
        budget_setting_hash_id: budget.hash_id,
      });
      if (status === 200) {
        toast.success(translate('calculation_overview.calculation_triggered'));
        scrollToTop();
      }
    } catch (err) {
      setError(errorMessage(err, 'trigger final calculation'));
      Sentry.captureException(err);
    }

    fetchAllBudgets();
  };

  const handleShowDebug = async (process) => {
    if (debugData) {
      setDebugData(null);
      return;
    }

    setError(null);

    try {
      const { status, data } = await api.get(`/calculation-engine/debug/${process.hash_id}`);

      if (status === 200) {
        setDebugData(data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch debug data'));
      Sentry.captureException(err);
    }
  };

  const handleEnableBatchCalculate = (e) => {
    e?.currentTarget?.blur();
    setBatchCalculateCheckedList([]);
    setEnableBatchCalculate(!enableBatchCalculate);
    setBatchCalculateAll(false);
  };

  const handleBatchCalculateChecked = (e, val) => {
    if (e.target.checked) {
      const newBatchCalculateCheckedList = [...batchCalculateCheckedList];
      newBatchCalculateCheckedList.push(val);
      setBatchCalculateCheckedList(newBatchCalculateCheckedList);
      if (newBatchCalculateCheckedList.length === budgetsData.length) {
        setBatchCalculateAll(true);
      }
    } else {
      setBatchCalculateAll(false);
      setBatchCalculateCheckedList(batchCalculateCheckedList.filter(v => v !== val));
    }
  };

  const handleBatchCalculateAll = (e) => {
    const checked = e.target.checked;
    setBatchCalculateAll(checked);

    if (checked) {
      setBatchCalculateCheckedList(budgetsData.map(b => b.hash_id));
    } else {
      setBatchCalculateCheckedList([]);
    }
  };

  const handleProcessBatchCalculate = async () => {
    setError(null);

    if (batchCalculateCheckedList.length < 1) return;

    try {
      const { status } = await api.post(`/calculation-engine/batch-calculation`, {
        budget_setting_hash_ids: [...batchCalculateCheckedList]
      });
      if (status === 200) {
        handleEnableBatchCalculate();
        toast.success(translate('calculation_overview.calculation_triggered'));
      }
    } catch (err) {
      setError(errorMessage(err, 'trigger batch calculate'));
      Sentry.captureException(err);
    }

    fetchAllBudgets();
  };

  const handleSelectBudget = (budget) => {
    setSelectedBudget(budget);
    setSelectedBudgetHistory(null);
    setDebugData(null);
    if (budget) fetchCalculationHistory(budget);

    if (searchParams.get('hash_id')) {
      searchParams.delete('hash_id');
      setSearchParams(searchParams);
    }
  };

  const fetchCalculations = async () => {
    setError(null);
    try {
      const { data, status } = await api.get(`/settings/calculation`);
      if (status === 200) {
        setCalculations(data.data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch calculation settings'));
      Sentry.captureException(err);
    }
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field, 'asc']);
    }
    setSortCols(newSortCols);
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field);
      setSortCols(sortCols.filter(sc => sc[0] === field));
    }
  };

  useEffect(() => {
    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  const handleSeedRequiredData = async (budget) => {
    setError(null);
    try {
      const { status } = await api.post(`/calculation-engine/seed/${budget.hash_id}`);
      if (status === 200) {
        toast.success(translate('calculation_overview.seed_required_data_triggered'));
      }
    } catch (err) {
      setError(errorMessage(err, 'trigger seed required data'));
      Sentry.captureException(err);
    }
    fetchAllBudgets();
  };

  const handleShowResults = (budget) => {
    navigate({ pathname: '/calculation/results', search: `?budget_hash_id=${budget.hash_id}` });
  };

  const sortBudgets = (arr, sortCols) => {
    arr.sort((a, b) => {
      for (const [key, direction] of sortCols) {
        let left, right;

        switch (key) {
          case 'group':
            left = (a.budget_group?.name || '').toLowerCase();
            right = (b.budget_group?.name || '').toLowerCase();
            break;
          case 'amount':
            left = (!a?.calculation_model?.is_without_budget && a.amount !== null) ? +formatNumberTo(a.amount, '.') : -1;
            right = (!b?.calculation_model?.is_without_budget && b.amount !== null) ? +formatNumberTo(b.amount, '.') : -1;
            break;
          case 'status':
            left = (a.current_process?.status || '');
            right = (b.current_process?.status || '');

            if (a.current_process?.status === 'completed' && a.current_process?.type === 'pre_calculation') {
              left = 'pre';
            }
            if (b.current_process?.status === 'completed' && b.current_process?.type === 'pre_calculation') {
              right = 'pre';
            }
            break;
          case 'last_calculation':
            left = -1;
            right = -1;

            if (a?.current_process && a.current_process?.status !== 'failed') {
              left = a.current_process?.status === 'in_progress'
                ? dayjs(a.current_process?.started_at).unix()
                : dayjs(a.current_process?.last_calculation_at).unix();
            }

            if (b?.current_process && b.current_process?.status !== 'failed') {
              right = b.current_process?.status === 'in_progress'
                ? dayjs(b.current_process?.started_at).unix()
                : dayjs(b.current_process?.last_calculation_at).unix();
            }
            break;
          default:
            left = a.name.toLowerCase();
            right = b.name.toLowerCase();
        }

        const multiplier = direction === 'asc' ? 1 : -1;
        if (left < right) return -1 * multiplier;
        if (left > right) return 1 * multiplier;
      }
      return 0;
    });
  };

  return <div className={`col-lg min-height-after-navbar`}>
    <div className="bg-white w-100 p-4 content-card h-100">
      <div ref={pinnedHeadRef} className="pinned-head">
        <div className="d-flex justify-content-between align-items-center border-bottom employee-management-tabs-div mb-4">
          <div className="employee-management-tabs">
            <div className={`cursor-pointer active`} onClick={() => navigate('/calculation/preliminary-check')} data-testid="not-calculated-tab">
              <span>{editableTranslate('calculation_overview.not_calculated')}</span>
            </div>
            <div className={`cursor-pointer`} onClick={() => navigate('/calculation/results')} data-testid="results-tab">
              <span>{editableTranslate('calculation_overview.results')}</span>
            </div>
          </div>
          <div className="position-relative">
            <Button variant="secondary" onClick={handleEnableBatchCalculate} data-testid="batch-calculate-button">
              {
                !enableBatchCalculate ? editableTranslate('calculation_overview.batch_calculate') : editableTranslate('calculation_overview.cancel_batch_calculate')
              }
            </Button>
            {
              enableBatchCalculate && <Button variant="primary" onClick={handleProcessBatchCalculate} disabled={batchCalculateCheckedList.length < 1} data-testid="calculate-button" className="ml-2"><i className="bi bi-calculator-fill"></i> {editableTranslate('calculation_test.calculate')} ({batchCalculateCheckedList.length} budgets)</Button>
            }
            <div className="employee-filters-separator"></div>
          </div>
        </div>

        {
          error &&
          <div className="mt-3">
            <ErrorAlert error={error} onClose={() => setError(null)} />
          </div>
        }

        <div className="row">
          <div className="col-4">
            <div className="stats-tile">
              <span className="tile-label">{editableTranslate('calculation_overview.calculated', 'Berechnet')}</span>
              <span>
                <span className="tile-value">{overview?.calculated_percent}%</span>
                <span className="tile-value-desc">{overview?.total_calculated} / {overview?.total}</span>
              </span>
            </div>
          </div>
          <div className="col-4">
            <div className="stats-tile">
              <span className="tile-label">{editableTranslate('calculation_overview.failed', 'Fehlgeschlagen')}</span>
              <span>
                <span className="tile-value">{overview?.total_failed}</span>
              </span>
            </div>
          </div>
          <div className="col-4">
            <div className="stats-tile">
              <span className="tile-label">{editableTranslate('calculation_overview.last_calculated', 'Letzte Berechnung')}</span>
              <span>
                <span className="tile-value">{overview?.last ? formatDateTime(overview?.last?.last_calculation_at, true) : ''}</span>
                <span className="tile-value-desc">{overview?.last ? formatTime(overview?.last?.last_calculation_at, true, true) : ''}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className={`${selectedBudget ? 'calculation-page-2-col d-flex' : ''} mt-2`}>
          <div className={`${selectedBudget ? 'calculation-left-div' : ''} mt-3`}>
            <div className="table-responsive has-custom-scrollbar">
              <table ref={tableRef} className="table calculation-overview-table" data-testid="budgets-table">
                <thead ref={theadRef} className="sticky-top">
                  <tr>
                    {
                      enableBatchCalculate && <th className="v-middle th-checkbox">
                        <input type="checkbox" checked={batchCalculateAll} onChange={handleBatchCalculateAll} data-testid="batch-calculate-all" />
                      </th>
                    }
                    <th scope="col" className="cursor-pointer">
                      <span className="float-right floating-icons">
                        <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['name'] ? (objSortCols['name'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('name')} data-testid="sort-name" />
                        {
                          objSortCols['name'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'name' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('name')} data-testid="sort-pin-name" />
                        }
                      </span>
                      <span className="th-span" onClick={() => handleChangeSortCol('name')} data-testid="header-name">
                        {getLabel('budget_setting.budget_name', 'Budget Name')}
                        <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                        <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                      </span>
                      {
                        !enableBatchCalculate && <div className="th-resize-div" data-field={'name'}></div>
                      }
                    </th>
                    {
                      !selectedBudget && <th scope="col" className="cursor-pointer">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['group'] ? (objSortCols['group'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('group')} data-testid="sort-group" />
                          {
                            objSortCols['group'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'group' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('group')} data-testid="sort-pin-group" />
                          }
                        </span>
                        <span className="th-span" onClick={() => handleChangeSortCol('group')} data-testid="header-group">
                          {getLabel('field.group', 'Group')}
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        {
                          !enableBatchCalculate && <div className="th-resize-div" data-field={'group'}></div>
                        }
                      </th>
                    }
                    {
                      !selectedBudget && <th scope="col" className="cursor-pointer text-right">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['amount'] ? (objSortCols['amount'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('amount')} data-testid="sort-amount" />
                          {
                            objSortCols['amount'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'amount' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('amount')} data-testid="sort-pin-amount" />
                          }
                        </span>
                        <span className="th-span" onClick={() => handleChangeSortCol('amount')} data-testid="header-amount">
                          {getLabel('budget_setting.budget_amount', 'Budget Amount')}
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        {
                          !enableBatchCalculate && <div className="th-resize-div" data-field={'amount'}></div>
                        }
                      </th>
                    }
                    <th scope="col" className="cursor-pointer text-left">
                      <span className="float-right floating-icons">
                        <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['status'] ? (objSortCols['status'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('status')} data-testid="sort-status" />
                        {
                          objSortCols['status'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'status' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('status')} data-testid="sort-pin-status" />
                        }
                      </span>
                      <span className="th-span" onClick={() => handleChangeSortCol('status')} data-testid="header-status">
                        {getLabel('calculation_overview.calculation_status', 'Calculation Status')}
                        <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                        <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                      </span>
                      {
                        !enableBatchCalculate && <div className="th-resize-div" data-field={'status'}></div>
                      }
                    </th>
                    {
                      !selectedBudget && <th scope="col" className="cursor-pointer text-left">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['last_calculation'] ? (objSortCols['last_calculation'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('last_calculation')} data-testid="sort-last-calculation" />
                          {
                            objSortCols['last_calculation'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'last_calculation' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('last_calculation')} data-testid="sort-pin-last-calculation" />
                          }
                        </span>
                        <span className="th-span" onClick={() => handleChangeSortCol('last_calculation')} data-testid="header-last-calculation">
                          {getLabel('calculation_overview.last_calculation', 'Letzte Berechnung')}
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        {
                          !enableBatchCalculate && <div className="th-resize-div" data-field={'last_calculation'}></div>
                        }
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  {budgetsData && budgetsData.map((budget, i) => (
                    <tr key={i} className={`${selectedBudget?.hash_id === budget.hash_id ? 'selected-row' : ''} table-row`} data-testid={`row-${budget?.hash_id}`}>
                      {
                        enableBatchCalculate && <td>
                          <input type="checkbox" checked={batchCalculateCheckedList.includes(budget.hash_id)} onChange={(e) => handleBatchCalculateChecked(e, budget.hash_id)} data-testid={`batch-calculate-${budget.hash_id}`} />
                        </td>
                      }
                      <td onClick={() => handleSelectBudget(budget)} data-testid="name-cell">
                        <TextTruncate text={budget.name} resizedCount={resizedCount} />
                      </td>
                      {!selectedBudget && <td onClick={() => handleSelectBudget(budget)} data-testid="group-cell">
                        <TextTruncate text={budget?.budget_group?.name} resizedCount={resizedCount} />
                      </td>}
                      {
                        !selectedBudget && <td className="text-right" onClick={() => handleSelectBudget(budget)} data-testid="amount-cell">
                          {
                            (!budget?.calculation_model?.is_without_budget && budget.amount !== null) ? <TextTruncate text={formatMoney(budget.amount)} resizedCount={resizedCount} /> : <i data-tooltip-id="no-budget-amount-tooltip" className="bi bi-exclamation-triangle-fill text-normal text-warning"></i>
                          }
                        </td>
                      }
                      <td onClick={() => handleSelectBudget(budget)} data-testid="status-cell">
                        <StatusBadge budget={budget} />
                      </td>
                      {
                        !selectedBudget && <td onClick={() => handleSelectBudget(budget)} data-testid="last-calculation-cell">
                          {
                            (budget?.current_process && budget.current_process?.status !== 'failed') && (
                              (budget.current_process?.status === 'in_progress' && budget.current_process.started_at) ? <TextTruncate text={translate('calculation_overview.started_at') + ': ' + formatTime(budget.current_process.started_at, true, true)} resizedCount={resizedCount} /> : <TextTruncate text={(budget.current_process.last_calculation_at && formatDateTime(budget.current_process.last_calculation_at, false, true, true))} resizedCount={resizedCount} />
                            )
                          }
                        </td>
                      }
                    </tr>
                  )
                  )}
                </tbody>
              </table>
            </div>
            <TableScrollbar tableRef={tableRef} data={budgetsData} trigger={resizedCount} />
          </div>
          {
            selectedBudget && <div className="mt-3 calculation-right-div">
              <div className="calculation-right-pinned">
                <div className="calculation-right-header">
                  <h5 className="font-weight-bold">{selectedBudget.name}</h5>
                  <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={() => handleSelectBudget(null)} data-testid="close-button"><i className="bi bi-x-lg"></i></span>
                </div>
                <div className="general-info-parent">
                  <div className="general-info">
                    <div className="row">
                      <div className="col">
                        <span>{editableTranslate('calculation_overview.calculation_status', 'Berechnungsstatus')}</span>
                        <span><StatusBadge budget={selectedBudget} /></span>
                      </div>
                      <div className="col">
                        <span>{editableTranslate('budget_setting.budget_amount')}</span>
                        <span className="info-field-value">
                          {
                            (!selectedBudget?.calculation_model?.is_without_budget && selectedBudget.amount !== null) ? formatMoney(selectedBudget.amount) : <i data-tooltip-id="no-budget-amount-tooltip" className="bi bi-exclamation-triangle-fill text-normal text-warning"></i>
                          }
                        </span>
                      </div>
                      {
                        selectedBudget.budget_group && <div className="col">
                          <span>{editableTranslate('field.group')}</span>
                          <span className="info-field-value">{selectedBudget.budget_group.name}</span>
                        </div>
                      }
                      <div className="col">
                        <span>{editableTranslate('budget_setting.calculation_model')}</span>
                        <span className="info-field-value">{selectedBudget.calculation_model?.name} <i className="bi bi-info-circle" data-tooltip-id={selectedBudget.calculation_model?.hash_id}></i></span>
                      </div>
                      <div className="col">
                        <span>{editableTranslate('calculation_overview.balanced_scorecard_in_budget')}</span>
                        <span className="info-field-value">{selectedBudget?.total_employee_balanced_scorecards} ({translate('calculation_overview.excluded_from_this').replace('$X', selectedBudget.calculation_processes?.pre_calculation?.other_data?.total_excluded_employee_balanced_scorecards || '0')})</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <hr />
                {
                  (selectedBudget.current_process?.type !== 'final_calculation' || selectedBudget.current_process?.status !== 'completed') && selectedBudget.current_process?.log && <div className="mb-3 px-0">
                    {/* <span className="font-weight-bold">{editableTranslate('form.notice', 'Notice')}</span> */}
                    <h5>{editableTranslate('calculation_overview.calculation_type.pre_calculation')} {examinationHelp ? helpTextIcon(examinationHelp?.key, examinationHelp?.short_text) : ''}</h5>
                    <p>{editableTranslate('calculation_overview.examination_description')}</p>
                    {
                      <ul className="list-group mt-1">
                        {
                          validationLogEntries.map(({ key, data, state }) => <LogListItem key={key} selectedBudget={selectedBudget} logKey={key} logData={data} state={state} />)
                        }
                      </ul>
                    }
                  </div>
                }
                <div className="pt-2">
                  <Button variant="primary" onClick={() => handlePreCalculation(selectedBudget)} data-testid="pre-calculation-button" className="mr-2"><i className="bi bi-calculator-fill"></i> {editableTranslate('calculation_overview.calculation_type.pre_calculation')}</Button>
                  <Button variant="primary" onClick={() => handleFinalCalculation(selectedBudget)} disabled={selectedBudget?.calculation_processes?.pre_calculation?.status === 'failed'} data-testid="final-calculation-button" className="mr-2"><i className="bi bi-calculator-fill"></i> {editableTranslate('calculation_overview.calculation_type.final_calculation')}</Button>
                  {
                    selectedBudget?.calculation_processes?.final_calculation?.status === 'completed' && <Button variant="primary" onClick={() => handleShowResults(selectedBudget)} data-testid="show-results-button" className="mr-2"><i className="bi bi-cash-coin mr-1"></i>{editableTranslate('calculation_overview.show_results', 'Ergebnisse anzeigen')}</Button>
                  }
                  {
                    (selectedBudget.current_process?.status === 'completed' || (selectedBudget.current_process?.type === 'final_calculation' && selectedBudget.current_process?.status === 'failed')) && <Button variant="primary" onClick={() => handleShowDebug(selectedBudget.current_process)} data-testid="debug-button"><i className="bi bi-bug-fill"></i> {editableTranslate('calculation_overview.debug')}</Button>
                  }
                  {
                    seedRequiredDataMode && <Button variant="primary" className="float-right" icon="bi-box-arrow-in-up-right" onClick={() => handleSeedRequiredData(selectedBudget)} data-testid="seed-data-button">{editableTranslate('calculation_overview.seed_required_data', 'Erforderliche Daten anlegen')}</Button>
                  }
                </div>
                {
                  debugData && <div className="mt-3">
                    <span className="font-weight-bold">{editableTranslate('calculation_overview.debug')} <Button variant="ghost" size="inline" icon="bi-eye-slash" onClick={() => setDebugData(null)} data-testid="hide-debug-button" /></span>
                    <textarea className="form-control mt-1" rows="20" value={JSON.stringify(debugData, null, 2)} readOnly data-testid="debug-output"></textarea>
                  </div>
                }
                <div className="mt-4">
                  <h5>{editableTranslate('calculation_overview.calculation_history', 'Historie')}</h5>
                  <hr />
                  <ul className="list-group">
                    {
                      selectedBudgetHistory && selectedBudgetHistory.map((history, index) => (
                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <span>{history.hash_id}</span>
                            {
                              history.started_at && <span className="text-muted ml-2">[start at: {formatDateTime(history.started_at, false, true, true)}]</span>
                            }
                          </div>
                          <div>
                            {
                              history.status === 'completed' && <span className="badge badge-success badge-pill">{editableTranslate('calculation_overview.status.completed', 'Abgeschlossen')}</span>
                            }
                            {
                              history.status === 'failed' && <span className="badge badge-danger badge-pill">{editableTranslate('calculation_overview.status.failed', 'Fehlgeschlagen')}</span>
                            }
                          </div>
                        </li>
                      ))
                    }
                    {
                      selectedBudgetHistory && selectedBudgetHistory.length < 1 && <li className="list-group-item">{editableTranslate('calculation_overview.no_calculation_history')}</li>
                    }
                  </ul>
                </div>
              </div>
            </div>
          }
        </div>
        <ReactTooltip id={'no-budget-amount-tooltip'} place="bottom">{editableTranslate('tooltips.calculation_overview.no_budget_amount')}</ReactTooltip>
        {
          calculations.map((c, i) => <ReactTooltip key={i} id={c.hash_id} className="react-tooltip-table" place="bottom" clickable={true}>
            <table>
              <tbody>
                <tr>
                  <td>{editableTranslate('calculation_settings.calculation_method')}</td>
                  <td>{editableTranslate('calculation_settings.' + c.full_settings?.calculation_method)}</td>
                </tr>

                <tr>
                  <td>{editableTranslate('calculation_settings.should_prorate_pay_parttime_employees')}</td>
                  <td>{editableTranslate((c.full_settings?.should_prorate_pay_parttime_employees === 'yes' ? 'form.yes' : 'form.no'))}</td>
                </tr>

                <tr>
                  <td>{editableTranslate('calculation_settings.define_how_rest_periods_affect')}</td>
                  <td>{c.full_settings?.how_rest_periods_affect_calculation ? editableTranslate('calculation_settings.how_rest_periods_affect.no_more_distributions') + ' ' + c.full_settings?.how_rest_periods_affect_calculation_amount : '-'}</td>
                </tr>

                <tr>
                  <td>{editableTranslate('calculation_settings.should_min_employment_specified')}</td>
                  <td>{c.full_settings?.how_many_months_at_least_amount} {editableTranslate('form.months')}</td>
                </tr>

                <tr>
                  <td>{editableTranslate('calculation_settings.should_premium_cap_defined')}</td>
                  <td>{editableTranslate((c.full_settings?.should_premium_cap_defined === 'yes' ? 'form.yes' : 'form.no'))}</td>
                </tr>

                <tr>
                  <td>{editableTranslate('calculation_settings.benefit_distribution_type')}</td>
                  <td>
                    {
                      c.full_settings?.benefit_distribution_type === 'with_configured_floor_percent' &&
                      editableTranslate('calculation_settings.benefit_distribution_type.with_configured_floor') + ' ' + c.full_settings?.with_configured_floor_percent + '%'
                    }
                    {
                      c.full_settings?.benefit_distribution_type === 'with_configured_floor_amount' &&
                      editableTranslate('calculation_settings.benefit_distribution_type.with_configured_floor') + ' ' + c.full_settings?.with_configured_floor_amount + '%'
                    }
                    {
                      !c.full_settings?.benefit_distribution_type.startsWith('with_configured_floor') && editableTranslate('calculation_settings.benefit_distribution_type.' + c.full_settings?.benefit_distribution_type)
                    }
                  </td>
                </tr>

                <tr>
                  <td>{editableTranslate('calculation_settings.shoud_use_fixed_amount_distribution')}</td>
                  <td>{editableTranslate((c.full_settings?.shoud_use_fixed_amount_distribution === 'yes' ? 'form.yes' : 'form.no'))}</td>
                </tr>
              </tbody>
            </table>
          </ReactTooltip>)
        }
      </div>
    </div>
  </div>;
}
